import React from 'react';
import Icon from '../../../components/AppIcon';
import { useAdminNotifications } from '../../../contexts/AdminNotificationContext';

const TOAST_STYLES = {
  critical: {
    border: 'border-red-500/40',
    bg: 'bg-red-500/10',
    iconBg: 'bg-red-500/20',
    iconColor: '#ef4444',
    titleColor: 'text-red-400',
    badge: 'bg-red-500',
    badgeText: 'CRITICAL',
  },
  warning: {
    border: 'border-orange-500/40',
    bg: 'bg-orange-500/10',
    iconBg: 'bg-orange-500/20',
    iconColor: '#f97316',
    titleColor: 'text-orange-400',
    badge: 'bg-orange-500',
    badgeText: 'WARNING',
  },
  'warning-yellow': {
    border: 'border-yellow-500/40',
    bg: 'bg-yellow-500/10',
    iconBg: 'bg-yellow-500/20',
    iconColor: '#eab308',
    titleColor: 'text-yellow-400',
    badge: 'bg-yellow-500',
    badgeText: 'WARNING',
  },
};

const formatTime = (date) => {
  if (!date) return '';
  return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const Toast = ({ toast, onDismiss }) => {
  const style = TOAST_STYLES?.[toast?.type] || TOAST_STYLES?.critical;

  return (
    <div
      className={`relative flex items-start gap-3 p-4 rounded-xl border ${style?.border} ${style?.bg} backdrop-blur-sm shadow-2xl w-80 animate-slide-in-right`}
      role="alert"
    >
      {/* Icon */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${style?.iconBg} flex items-center justify-center mt-0.5`}>
        <Icon name={toast?.icon || 'AlertTriangle'} size={18} color={style?.iconColor} />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${style?.badge} text-white`}>
            {style?.badgeText}
          </span>
        </div>
        <p className={`text-sm font-semibold ${style?.titleColor} leading-tight`}>{toast?.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{toast?.message}</p>
        <p className="text-[10px] text-muted-foreground/60 mt-1">{formatTime(toast?.timestamp)}</p>
      </div>
      {/* Dismiss */}
      <button
        onClick={() => onDismiss(toast?.id)}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss notification"
      >
        <Icon name="X" size={13} />
      </button>
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-b-xl overflow-hidden">
        <div
          className={`h-full ${style?.badge} opacity-60 animate-shrink-width`}
          style={{ animationDuration: '6s', animationTimingFunction: 'linear', animationFillMode: 'forwards' }}
        />
      </div>
    </div>
  );
};

const ToastContainer = () => {
  const { toasts, dismissToast } = useAdminNotifications();

  if (!toasts?.length) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[200] flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts?.map((toast) => (
        <div key={toast?.id} className="pointer-events-auto">
          <Toast toast={toast} onDismiss={dismissToast} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
